// Prev/next navigation between a book's reading units (front matter →
// chapters → back matter, PD-2). Two renderings of the same `adjacentUnits`
// data: compact header chevrons (always present in the reading view) and a
// book-like page-turn footer at the end of the text column (single-column
// mode only — see DocumentPane's `footer` prop note).

import type { ReactNode } from "react";
import { Link } from "react-router";
import { ChevronLeft, ChevronRight, Lock } from "lucide-react";
import { Button } from "@bindery/ui/components/ui/button";
import { cn } from "@/lib/utils";
import { unitLabel, unitPath, type ReadingUnit } from "./reading-order";

interface UnitNavProps {
  /** Routes the links AND names the units — `unitLabel` reads the book's own
   *  division labels from it ("Chapter 5", "Book α", "Letter 3"). */
  bookSlug: string;
  prev: ReadingUnit | null;
  next: ReadingUnit | null;
}

/** Compact prev/next chevrons for the reading-view toolbar. */
export function UnitNavButtons({ bookSlug, prev, next }: UnitNavProps) {
  return (
    <div className="flex items-center">
      {([prev, next] as const).map((unit, i) => {
        const isNext = i === 1;
        const Icon = isNext ? ChevronRight : ChevronLeft;
        const label = isNext ? "Next" : "Previous";
        if (!unit) {
          return (
            <Button
              key={label}
              type="button"
              variant="ghost"
              size="icon-sm"
              className="pointer-coarse:min-h-11 pointer-coarse:min-w-11"
              disabled
              aria-label={label}
            >
              <Icon className="size-4" />
            </Button>
          );
        }
        const isLocked = unit.kind === "chapter" && unit.premium;
        return (
          <Button
            key={label}
            asChild
            variant="ghost"
            size="icon-sm"
            className="pointer-coarse:min-h-11 pointer-coarse:min-w-11"
            aria-label={`${label}: ${unitLabel(unit, bookSlug)}${isLocked ? " (locked)" : ""}`}
            title={unitLabel(unit, bookSlug)}
          >
            <Link to={unitPath(bookSlug, unit)} className="relative">
              <Icon className="size-4" />
              {isLocked && (
                <Lock
                  aria-hidden="true"
                  className="text-muted-foreground absolute right-0.5 bottom-0.5 size-2.5"
                />
              )}
            </Link>
          </Button>
        );
      })}
    </div>
  );
}

function PageTurnLink({
  bookSlug,
  unit,
  side,
}: {
  bookSlug: string;
  unit: ReadingUnit;
  side: "prev" | "next";
}) {
  const isNext = side === "next";
  // A chapter unit shows "Chapter N" over its title; a matter unit's label IS
  // its title, so the second line would just repeat it — show one line.
  const heading = unitLabel(unit, bookSlug);
  const title = unit.kind === "chapter" ? unit.title : null;
  const isLocked = unit.kind === "chapter" && unit.premium;
  return (
    <Link
      to={unitPath(bookSlug, unit)}
      className={cn(
        "group flex max-w-[16em] flex-col gap-1 no-underline",
        isNext ? "items-end text-right" : "items-start text-left",
      )}
    >
      <span className="text-muted-foreground flex items-center gap-1.5 text-[0.65em] tracking-[0.25em] uppercase">
        {!isNext && (
          <span aria-hidden="true" className="font-sans">
            ←
          </span>
        )}
        {isNext ? "Next" : "Previous"}
        {isNext && (
          <span aria-hidden="true" className="font-sans">
            →
          </span>
        )}
      </span>
      <span className="font-display text-foreground decoration-border flex items-center gap-1.5 text-[1.05em] leading-snug font-bold text-balance underline-offset-4 group-hover:underline">
        {isLocked && (
          <Lock
            aria-label="Locked"
            className="text-muted-foreground size-[0.85em] shrink-0"
          />
        )}
        {heading}
      </span>
      {title !== null && title !== heading && (
        <span className="text-muted-foreground text-[0.85em] leading-snug">
          {title}
        </span>
      )}
    </Link>
  );
}

/**
 * Book-like page-turn footer, rendered as the text column's last element. Font
 * scales with the pane (em-relative) like the prose above it.
 *
 * `volumeEnd` is the end-of-volume slot (`volume-end.tsx`), and it takes the
 * NEXT link's position rather than a corner of its own: a reader who has just
 * finished a volume is at a boundary, and the boundary is what that side of the
 * footer is for. When the book continues into another volume the page-turn
 * simply sits beneath it — the mark is offered first, the turn stays available.
 */
export function PageTurnNav({
  bookSlug,
  prev,
  next,
  volumeEnd,
}: UnitNavProps & { volumeEnd?: ReactNode }) {
  // `volumeEnd` is an ELEMENT when supplied — `<VolumeEnd />` decides for itself
  // whether this unit ends a volume and renders nothing when it doesn't — so
  // this guard collapses the footer only for a caller that omits the slot
  // entirely. Both reading views pass it; a book of one unit therefore keeps
  // its mark control rather than losing the footer that holds it.
  if (!prev && !next && volumeEnd === undefined) return null;
  return (
    <nav
      aria-label="Book navigation"
      className="border-border mt-[3em] flex items-start justify-between gap-6 border-t pt-[1.5em]"
    >
      {prev ? (
        <PageTurnLink bookSlug={bookSlug} unit={prev} side="prev" />
      ) : (
        <span />
      )}
      <div className="flex flex-col items-end gap-[1.4em]">
        {volumeEnd}
        {next && <PageTurnLink bookSlug={bookSlug} unit={next} side="next" />}
      </div>
    </nav>
  );
}
